/**
 * ASCII Art Generator - HTML+JS Background 导出器
 *
 * 导出可嵌入网站的 ASCII 艺术背景，支持自适应容器、离屏暂停等功能
 * Wave 5 完整实现
 * Wave 3 Phase 6.5: 引用完整打包运行时
 */

import type { HtmlJsExportConfig, AsciiBgRuntimeConfig } from './types'
import { DEFAULT_HTMLJS_EXPORT_CONFIG } from './types'
import type { FullRenderSettings } from '../types'
import type { EffectsState } from '../effects/types'
import { createAsciiRuntimeConfig } from './runtime-config'
import { getRuntimeContent, getImageDimensions, extractEnabledEffects } from './utils'

// ============================================
// HTML+JS 导出结果
// ============================================

export interface HtmlJsExportOutput {
  /** HTML 内容 */
  content: string
  /** 外部 JS 文件内容（分离模式） */
  externalJsContent?: string
  /** 外部 JS 文件名 */
  externalJsFilename?: string
  /** 需要 ZIP 打包运行时（分离模式） */
  needsRuntimeDownload?: boolean
  /** 文件名 */
  filename: string
}

// ============================================
// 运行时版本信息
// ============================================

const RUNTIME_VERSION = '1.0.0'
const RUNTIME_FILENAME = 'ascii-bg-runtime.js'

// ============================================
// HTML+JS 导出器类
// ============================================

/**
 * HTML+JS Background 导出器
 */
export class HtmlJsExporter {
  /**
   * 导出 HTML+JS Background
   *
   * @param canvas 渲染的 Canvas（用于获取当前状态）
   * @param sourceImage 源图片（HTMLImageElement 或 Base64 字符串）
   * @param settings 渲染设置
   * @param effectsState 特效状态
   * @param config 导出配置
   */
  async export(
    _canvas: HTMLCanvasElement,
    sourceImage: HTMLImageElement | string,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    config: Partial<HtmlJsExportConfig> = {}
  ): Promise<HtmlJsExportOutput> {
    const finalConfig = { ...DEFAULT_HTMLJS_EXPORT_CONFIG, ...config }

    // 从 settings 和 effectsState 生成运行时配置
    const runtimeConfig = this.createRuntimeConfig(settings, effectsState, finalConfig, sourceImage)

    // 获取图片数据
    const imageData = await this.getImageData(sourceImage, finalConfig)

    // 获取完整运行时内容
    const runtimeContent = await getRuntimeContent()

    // 内联模式：完整运行时内嵌到 HTML
    // 分离模式：HTML 引用外部运行时，ZIP 打包时包含
    if (finalConfig.outputMode === 'inline') {
      return this.generateInlineHtml(runtimeConfig, imageData, runtimeContent, finalConfig)
    } else {
      return this.generateSplitHtml(runtimeConfig, imageData, runtimeContent, finalConfig)
    }
  }

  /**
   * 从设置创建运行时配置
   */
  private createRuntimeConfig(
    settings: FullRenderSettings,
    effectsState: EffectsState,
    exportConfig: HtmlJsExportConfig,
    sourceImage?: HTMLImageElement | string
  ): AsciiBgRuntimeConfig {
    return createAsciiRuntimeConfig(settings, effectsState, exportConfig, sourceImage)

    // 使用共享工具函数提取启用的特效
    const { enabledEffects, effectsConfig } = extractEnabledEffects(effectsState)

    // 获取图片尺寸
    const dims = getImageDimensions(sourceImage || '')
    const imageWidth = dims.width
    const imageHeight = dims.height

    return {
      engine: settings?.style || exportConfig.engine,
      animation: settings?.animationType || exportConfig.animation,
      animationParams: {
        amplitude: settings?.ditherWaveAmplitude || 0.3,
        frequency: settings?.ditherWaveFrequency || 3,
        speed: settings?.ditherWaveSpeed || 0.05
      },
      effects: enabledEffects,
      effectsConfig: effectsConfig,
      transparent: exportConfig.transparent,
      alphaMask: exportConfig.alphaMask,
      pauseOffScreen: exportConfig.pauseOffScreen,
      interaction: exportConfig.interaction || settings?.ditherMouseInteractionEnabled || settings?.dither2MouseInteractionEnabled || false,
      fadeIn: exportConfig.fadeIn,
      adaptivePerf: exportConfig.adaptivePerf,
      targetFPS: 60,

      // === 渲染参数（所见即所得） ===
      fontSize: settings?.fontSize || 10,
      charSpacing: settings?.charSpacing || 1,
      contrast: settings?.contrast || 1,
      brightness: settings?.brightness || 0,
      opacity: settings?.opacity || 1,
      invert: settings?.invert || false,
      backgroundColor: settings?.backgroundColor || '#000000',
      colorMode: settings?.colorMode || 'grayscale',
      customColor: settings?.customColor || '#00ff99',
      ditherStrength: settings?.ditherStrength || 0.8,
      halftoneSize: settings?.halftoneSize || 1,
      particleDensity: settings?.particleDensity || 0.5,
      claudeDensity: settings?.claudeDensity || 0.7,
      retroNoise: settings?.retroNoise || 0.45,
      lineLength: settings?.lineLength || 1,
      lineThickness: settings?.lineThickness || 1.6,

      // 字符集参数
      charset: settings?.charset,
      customCharset: settings?.customCharset,
      brailleVariant: settings?.brailleVariant,
      terminalCharset: settings?.terminalCharset,
      renderFont: settings?.renderFont,

      // 图片比例
      imageWidth,
      imageHeight,

      // === 鼠标交互参数（所见即所得） ===
      mouseInteractionMode: settings?.mouseInteractionMode || 'attract',
      ditherMouseRadius: settings?.ditherMouseRadius || 0.3,
      ditherMouseInteractionEnabled: settings?.ditherMouseInteractionEnabled || false,
      dither2MouseInteractionEnabled: settings?.dither2MouseInteractionEnabled || false,
      hoverStrength: settings?.hoverStrength || 24,
      mouseAreaSize: settings?.mouseAreaSize || 180,
      mouseSpread: settings?.mouseSpread || 1,

      // === 旧版兼容参数名（成功版本的运行时） ===
      animationEnabled: settings?.ditherAnimationEnabled || settings?.dither2AnimationEnabled || false,
      mouseInteractionEnabled: settings?.ditherMouseInteractionEnabled || settings?.dither2MouseInteractionEnabled || false,
      mouseInfluenceRadius: settings?.mouseAreaSize || 180,
      mouseInfluenceStrength: settings?.mouseSpread || 1
    }
  }

  /**
   * 获取图片数据
   */
  private async getImageData(
    sourceImage: HTMLImageElement | string,
    config: HtmlJsExportConfig
  ): Promise<string> {
    // 如果是字符串（URL 或 Base64）
    if (typeof sourceImage === 'string') {
      return sourceImage
    }

    // 如果是 HTMLImageElement
    if (sourceImage instanceof HTMLImageElement) {
      // 外链模式
      if (config.imageEmbedMode === 'external' && config.externalImageUrl) {
        return config.externalImageUrl
      }

      // 嵌入模式：转换为 Base64
      try {
        const canvas = document.createElement('canvas')
        canvas.width = sourceImage.naturalWidth || sourceImage.width
        canvas.height = sourceImage.naturalHeight || sourceImage.height

        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(sourceImage, 0, 0)
          return canvas.toDataURL('image/jpeg', 0.85)
        }
      } catch (e) {
        console.warn('Failed to convert image to Base64:', e)
      }

      // 如果有 src 属性，使用它
      if (sourceImage.src) {
        return sourceImage.src
      }
    }

    throw new Error('Unable to get image data')
  }

  /**
   * 生成内联 HTML（完整运行时内嵌）
   */
  private generateInlineHtml(
    config: AsciiBgRuntimeConfig,
    imageData: string,
    runtimeContent: string,
    exportConfig: HtmlJsExportConfig
  ): HtmlJsExportOutput {

    // 如果运行时内容为空，添加警告
    const runtimeWarning = !runtimeContent
      ? `// ⚠️ 警告：运行时内容获取失败，请确保已构建运行时 (npm run build:runtime)
   // 导出的 HTML 可能无法正常工作`
      : ''

    // 是否隐藏默认内容
    const hideContent = exportConfig.hideDefaultContent !== false

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ASCII Art Background</title>
  <style>
    /* 容器样式 - 保持图片比例 */
    .ascii-bg-container {
      position: relative;
      width: 100%;
      max-width: ${config.imageWidth || 800}px;
      aspect-ratio: ${config.imageWidth || 16}/${config.imageHeight || 9};
      min-height: 200px;
      overflow: hidden;
      background: ${config.backgroundColor || '#000'};
      margin: 0 auto;
    }

    /* Canvas 样式 */
    .ascii-bg-container canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    /* 内容层（如果有） */
    .ascii-bg-content {
      position: relative;
      z-index: 10;
      padding: 20px;
      color: #fff;
      text-align: center;
    }
  </style>
</head>
<body>
  <!-- ASCII Background Container -->
  <div class="ascii-bg-container"
       data-ascii-bg
       data-config='${JSON.stringify(config)}'
       data-image="${imageData}">
    ${hideContent ? '' : `
    <!-- 内容层（可选） -->
    <div class="ascii-bg-content">
      <h1>Your Content Here</h1>
      <p>ASCII Background 作为背景渲染</p>
    </div>
    `}
  </div>

  <!-- 完整运行时 Script (内嵌) -->
  <script>
  ${runtimeWarning}
  ${runtimeContent}

  // 初始化
  document.addEventListener('DOMContentLoaded', function() {
    const containers = document.querySelectorAll('[data-ascii-bg]');
    containers.forEach(function(container) {
      const configAttr = container.getAttribute('data-config');
      const config = configAttr ? JSON.parse(configAttr) : {};
      const imageSrc = container.getAttribute('data-image') || '';
      if (window.AsciiBg) {
        AsciiBg.init(container, config, imageSrc);
      }
    });
  });
  </script>
</body>
</html>`

    return {
      content: html,
      filename: `ascii-bg-${Date.now()}.html`
    }
  }

  /**
   * 生成分离 HTML+JS（外部 JS 文件）
   *
   * HTML 引用外部运行时，ZIP 打包时自动包含完整运行时
   */
  private generateSplitHtml(
    config: AsciiBgRuntimeConfig,
    imageData: string,
    runtimeContent: string,
    exportConfig: HtmlJsExportConfig
  ): HtmlJsExportOutput {

    // 是否隐藏默认内容
    const hideContent = exportConfig.hideDefaultContent !== false

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ASCII Art Background</title>
  <style>
    /* 容器样式 - 保持图片比例 */
    .ascii-bg-container {
      position: relative;
      width: 100%;
      max-width: ${config.imageWidth || 800}px;
      aspect-ratio: ${config.imageWidth || 16}/${config.imageHeight || 9};
      min-height: 200px;
      overflow: hidden;
      background: ${config.backgroundColor || '#000'};
      margin: 0 auto;
    }

    /* Canvas 样式 */
    .ascii-bg-container canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    /* 内容层（如果有） */
    .ascii-bg-content {
      position: relative;
      z-index: 10;
      padding: 20px;
      color: #fff;
      text-align: center;
    }
  </style>
</head>
<body>
  <!-- ASCII Background Container -->
  <div class="ascii-bg-container"
       data-ascii-bg
       data-config='${JSON.stringify(config)}'
       data-image="${imageData}">
    ${hideContent ? '' : `
    <!-- 内容层（可选） -->
    <div class="ascii-bg-content">
      <h1>Your Content Here</h1>
      <p>ASCII Background 作为背景渲染</p>
    </div>
    `}
  </div>

  <!-- External Runtime Script -->
  <script src="ascii-bg-runtime.js"></script>

  <!-- 初始化脚本 -->
  <script>
    document.addEventListener('DOMContentLoaded', function() {
      const containers = document.querySelectorAll('[data-ascii-bg]');
      containers.forEach(function(container) {
        const configAttr = container.getAttribute('data-config');
        const config = configAttr ? JSON.parse(configAttr) : {};
        const imageSrc = container.getAttribute('data-image') || '';
        if (window.AsciiBg) {
          AsciiBg.init(container, config, imageSrc);
        }
      });
    });
  </script>
</body>
</html>`

    return {
      content: html,
      externalJsContent: runtimeContent, // ZIP 打包时使用
      externalJsFilename: RUNTIME_FILENAME,
      needsRuntimeDownload: !runtimeContent, // 如果运行时内容为空，标记需要下载
      filename: `ascii-bg-${Date.now()}.html`
    }
  }

  /**
   * 清除运行时缓存
   */
  clearCache(): void {
    // 使用共享工具函数的缓存清除
  }

  /**
   * 获取完整运行时信息
   */
  getRuntimeInfo(): { filename: string; version: string; size: string; features: string[] } {
    return {
      filename: RUNTIME_FILENAME,
      version: RUNTIME_VERSION,
      size: '~125KB (gzip ~30KB)',
      features: [
        '11 渲染引擎',
        '12 动画效果',
        '8 叠加特效',
        '离屏暂停',
        '自适应性能监控',
        'Alpha 遮罩渐变',
        '鼠标交互'
      ]
    }
  }
}

// ============================================
// 工厂函数
// ============================================

export function createHtmlJsExporter(): HtmlJsExporter {
  return new HtmlJsExporter()
}
